import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import TransferTabs from '../components/TransferTabs';
import QuickActions from '../components/QuickActions';
import { 
  getUserData, 
  subscribeToAccountUpdates, 
  subscribeToTransactions,
  processTransfer,
  updateAccountNumbers,
  getDisplayName,
  getFirstName
} from '../utils/firestore';
import { MOCK_TRANSACTIONS } from '../utils/mockData';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

// ImgBB API key
const IMGBB_API_KEY = '9aa0ca74fa1635cd69532671265a980f';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTransactions, setAllTransactions] = useState([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [profileImage, setProfileImage] = useState('');

  // Format currency with commas and decimals
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format account number in 3-3-4 groups
  const formatAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = number.replace(/\s/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }
    return number;
  };

  // Copy account number to clipboard
  const handleCopyClick = async (accountNumber, accountType) => {
    try {
      await navigator.clipboard.writeText(accountNumber.replace(/\s/g, ''));
      setCopiedAccount(accountType);
      setTimeout(() => setCopiedAccount(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get checking account available balance
  const getCheckingBalance = (accounts) => {
    const checkingAccount = accounts.find(acc => acc.type === 'Checking');
    return checkingAccount ? checkingAccount.available : 0;
  };

  const uploadToImgBB = async (file) => {
    try {
      console.log('Starting ImgBB upload for file:', file.name);
      
      const formData = new FormData();
      formData.append('image', file);

      // Log the API request details
      console.log('Sending request to ImgBB API...');
      
      const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ImgBB API response:', data);

      if (data.success) {
        console.log('Image uploaded successfully. URL:', data.data.url);
        return data.data.url;
      } else {
        console.error('ImgBB API error:', data.error);
        throw new Error(data.error?.message || 'Failed to upload image to ImgBB');
      }
    } catch (error) {
      console.error('Error uploading to ImgBB:', error);
      throw error;
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('Selected file:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image size should be less than 5MB');
      return;
    }

    try {
      console.log('Starting image upload process...');
      
      // Upload to ImgBB
      const imageUrl = await uploadToImgBB(file);
      console.log('Received image URL from ImgBB:', imageUrl);
      
      if (!imageUrl) {
        throw new Error('No image URL received from ImgBB');
      }

      // Update Firestore with the new image URL
      const userRef = doc(db, 'users', currentUser.uid);
      console.log('Updating Firestore with new image URL...');
      
      await updateDoc(userRef, {
        profileImage: imageUrl
      });
      console.log('Firestore update successful');

      // Update local state
      setProfileImage(imageUrl);
      setErrorMessage('');
    } catch (error) {
      console.error('Error in handleImageChange:', error);
      setErrorMessage('Failed to update profile image. Please try again.');
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) return;
    console.log('Setting up Dashboard subscriptions for user:', currentUser.uid);

    const loadInitialData = async () => {
      try {
        const data = await getUserData(currentUser.uid);
        console.log('Loaded user data:', data);
        console.log('Joint account data:', {
          isJointAccount: data.isJointAccount,
          jointAccountHolder: data.jointAccountHolder,
          fullJointAccountHolder: JSON.stringify(data.jointAccountHolder, null, 2)
        });
        
        // Update account numbers if needed
        const updatedAccounts = await updateAccountNumbers(currentUser.uid);
        if (updatedAccounts) {
          setAccounts(updatedAccounts);
        } else {
          setAccounts(data.accounts || []);
        }
        
        setUserData(data);
        
        // Check if user is new (has registrationDate)
        setIsNewUser(!!data.registrationDate);
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // Subscribe to account updates
    const unsubscribeAccounts = subscribeToAccountUpdates(
      currentUser.uid,
      (updatedAccounts) => {
        console.log('New accounts data:', updatedAccounts);
        setAccounts(updatedAccounts);
      }
    );

    // Subscribe to transactions for all users
    const unsubscribeTransactions = subscribeToTransactions(
      currentUser.uid,
      (transactions) => {
        console.log('Received transactions update:', transactions);
        setAllTransactions(transactions);
      }
    );

    return () => {
      console.log('Cleaning up Dashboard subscriptions');
      unsubscribeAccounts();
      unsubscribeTransactions();
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    const loadUserData = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            // Set profile image from Firestore
            if (data.profileImage) {
              setProfileImage(data.profileImage);
            }
          }
        } catch (err) {
          console.error('Error loading user data:', err);
        }
      }
    };

    loadUserData();
  }, [currentUser]);

  // Add error handling for image loading
  const handleImageError = () => {
    console.error('Failed to load profile image');
    setProfileImage('');
  };

  const handleTransfer = async (transferData) => {
    try {
      await processTransfer({
        ...transferData,
        userId: currentUser.uid
      });
    } catch (error) {
      console.error('Transfer error:', error);
      setErrorMessage('An error occurred while processing your transfer. Please try again or contact support if the issue persists.');
      throw error;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const getTransactionIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'transfer':
        return '↔️';
      case 'bill pay':
        return '💰';
      case 'deposit':
        return '📱';
      default:
        return '💳';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  console.log('Current render state:', {
    userData,
    accounts,
    transactions: allTransactions,
    currentUser: currentUser?.uid
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        </main>
      </div>
    );
  }

  const checkingBalance = getCheckingBalance(accounts);
  const checkingAccount = accounts.find(acc => acc.type === 'Checking');
  const checkingAccountNumber = checkingAccount?.accountNumber || '';

  const handleViewTransactions = (account) => {
    // Scroll to transactions section
    const transactionsSection = document.querySelector('.recent-transactions');
    if (transactionsSection) {
      transactionsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderAccountInfo = (account) => {
    console.log('Rendering account info with userData:', userData);
    console.log('Joint account details:', {
      isJointAccount: userData?.isJointAccount,
      jointAccountHolder: userData?.jointAccountHolder,
      primaryName: userData?.name
    });
    
    return (
      <div key={account.id} className="bg-white rounded-lg shadow p-6 mb-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">{account.type} Account</h3>
              {userData?.isJointAccount && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">
                  Joint Account
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">Account Number: {formatAccountNumber(account.accountNumber)}</p>
            {userData?.isJointAccount && userData?.jointAccountHolder?.name && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">
                  Account Holders: {getDisplayName(userData)} & {userData.jointAccountHolder.name}
                </p>
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Available Balance</p>
            <p className="text-2xl font-bold text-gray-900">${account.available.toFixed(2)}</p>
            <p className="text-sm text-gray-500">Current Balance</p>
            <p className="text-xl font-semibold text-gray-700">${account.balance.toFixed(2)}</p>
          </div>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => handleTransfer(account)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Transfer
          </button>
          <button
            onClick={() => handleViewTransactions(account)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            View Transactions
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] pt-24 md:pt-28">
      <Navigation />
      
      {/* Header Section */}
      <header className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
        {/* Decorative patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-white/20 blur-3xl transform rotate-12"></div>
          <div className="absolute -left-20 -bottom-20 w-96 h-96 rounded-full bg-white/20 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-white/10 blur-2xl"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.1),transparent)]"></div>
          </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            {/* Left Section - Welcome Message */}
            <div className="flex items-start space-x-5">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center transform transition-all duration-300 hover:scale-110 hover:rotate-12 group">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-2xl"
                      onError={handleImageError}
                    />
                  ) : (
                    <span className="text-3xl transform transition-transform duration-300 group-hover:scale-110">👋</span>
                  )}
                  <div className="absolute inset-0 bg-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-400 border-2 border-white flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-white/70">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                  <span className="w-1 h-1 rounded-full bg-white/40"></span>
                  <p className="text-sm text-white/70">
                    {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
                <h1 className="text-3xl font-semibold text-white">
                  Welcome back, {getFirstName(userData) || 'there'}
                  {userData?.isJointAccount && (
                    <span className="text-lg ml-2 text-white/80">
                      (Joint Account)
                    </span>
                  )}
                </h1>
              </div>
            </div>

            {/* Right Section - Balance & Quick Stats */}
            <div className="w-full lg:w-auto grid grid-cols-1 sm:grid-cols-3 lg:flex lg:items-center gap-4 lg:space-x-6">
              {/* Available Balance */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 lg:min-w-[200px]">
                <p className="text-sm text-white/70 mb-1">Available Balance</p>
                <p className="text-2xl font-semibold text-white">{formatCurrency(checkingBalance)}</p>
                <div className="flex items-center mt-2">
                  <div className="w-full bg-white/20 h-1 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: '75%' }}></div>
                  </div>
                </div>
              </div>

              {/* Pending Transactions */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                <p className="text-sm text-white/70 mb-1">Pending</p>
                <p className="text-2xl font-semibold text-white">
                  {formatCurrency(allTransactions
                    .filter(t => t.status === 'pending')
                    .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                  )}
                </p>
                <p className="text-sm text-white/70 mt-2">
                  {allTransactions.filter(t => t.status === 'pending').length} transactions
                </p>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center space-x-2">
                <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-3 transition-colors duration-200">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
                <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-3 transition-colors duration-200">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </button>
                <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-3 transition-colors duration-200">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Account Overview */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            {/* Card Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center">
                  <span className="text-2xl">💳</span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Checking Account</h2>
                  <p className="text-sm text-gray-500">Personal Banking</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  Active
                </span>
                <div className="w-px h-6 bg-gray-200"></div>
              </div>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Account Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Balance Section */}
                <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-6">
                  <p className="text-sm font-medium text-primary-600 mb-1">Available Balance</p>
                  <p className="text-3xl font-semibold text-gray-900">{formatCurrency(checkingBalance)}</p>
                  <div className="mt-4 flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Personal Account
                    </span>
                  </div>
                </div>

                {/* Account Numbers Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Account Number */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-500 mb-1">Account Number</p>
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-lg text-gray-900">{formatAccountNumber(checkingAccountNumber)}</p>
                      <button
                        onClick={() => handleCopyClick(checkingAccountNumber, 'checking')}
                        className="p-2 rounded-lg hover:bg-white transition-colors duration-200"
                        title="Copy account number"
                      >
                        {copiedAccount === 'checking' ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-gray-400 hover:text-gray-600">📋</span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Routing Number */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-500 mb-1">Routing Number</p>
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-lg text-gray-900">{checkingAccount?.routingNumber || '021000021'}</p>
                      <button 
                        onClick={() => handleCopyClick(checkingAccount?.routingNumber || '021000021', 'routing')}
                        className="p-2 rounded-lg hover:bg-white transition-colors duration-200"
                        title="Copy routing number"
                      >
                        {copiedAccount === 'routing' ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <span className="text-gray-400 hover:text-gray-600">📋</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Customer Info */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Account Information</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Account Holder</p>
                    <p className="text-base font-medium text-gray-900">
                      {userData?.isJointAccount && userData?.jointAccountHolder?.name
                        ? `${getDisplayName(userData)} & ${userData.jointAccountHolder.name}`
                        : getDisplayName(userData) || 'Account Holder'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Account Type</p>
                    <p className="text-base font-medium text-gray-900">Personal Checking</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Account Status</p>
                    <div className="flex items-center mt-1">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <p className="text-base font-medium text-gray-900">Active</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          {/* Monthly Income Card */}
          <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-50 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-500"></div>
            <div className="relative">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-600">Monthly Income</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {formatCurrency(allTransactions
                      .filter(t => {
                        const now = new Date();
                        const txDate = new Date(t.date);
                        return t.type === 'credit' &&
                          txDate.getMonth() === now.getMonth() &&
                          txDate.getFullYear() === now.getFullYear();
                      })
                      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <div className="flex-1">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-500">
                  {allTransactions
                    .filter(t => {
                      const now = new Date();
                      const txDate = new Date(t.date);
                      return t.type === 'credit' &&
                        txDate.getMonth() === now.getMonth() &&
                        txDate.getFullYear() === now.getFullYear();
                    }).length} deposits
                </span>
              </div>
            </div>
          </div>

          {/* Monthly Outgoing Card */}
          <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute -right-16 -top-16 w-32 h-32 bg-red-50 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-500"></div>
            <div className="relative">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-600">Monthly Outgoing</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {formatCurrency(allTransactions
                      .filter(t => {
                        const now = new Date();
                        const txDate = new Date(t.date);
                        return t.type === 'debit' &&
                          txDate.getMonth() === now.getMonth() &&
                          txDate.getFullYear() === now.getFullYear();
                      })
                      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <div className="flex-1">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-red-500 h-1.5 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-500">
                  {allTransactions
                    .filter(t => {
                      const now = new Date();
                      const txDate = new Date(t.date);
                      return t.type === 'debit' &&
                        txDate.getMonth() === now.getMonth() &&
                        txDate.getFullYear() === now.getFullYear();
                    }).length} payments
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Transfer Money Section */}
        <div id="transfer-section" className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Transfer Money</h2>
                <p className="text-sm text-gray-500">Send money securely to accounts and other users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <TransferTabs 
              accounts={accounts} 
              onTransfer={handleTransfer}
              currentUser={currentUser}
            />
          </div>
        </div>

        {/* Quick Actions Section */}
        <div id="quick-actions" className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <QuickActions accounts={accounts} />
        </div>

        {/* Recent Transactions */}
        {allTransactions.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden recent-transactions">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
                <button 
                  onClick={() => {
                    const quickActionsSection = document.getElementById('quick-actions');
                    quickActionsSection.scrollIntoView({ behavior: 'smooth' });
                    // Find the statements button and click it
                    setTimeout(() => {
                      const statementsButton = document.querySelector('[data-action-id="statements"]');
                      if (statementsButton) {
                        statementsButton.click();
                      }
                    }, 500);
                  }}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View All
                </button>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {allTransactions.slice(0, 20).map((transaction, index) => (
                <div key={transaction.id || index} className="p-4 hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                        <span className="text-lg">{getTransactionIcon(transaction.category)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{transaction.description}</p>
                        <p className="text-sm text-gray-500">{formatDate(transaction.date)}</p>
                      </div>
                    </div>
                    <p className={`font-medium ${
                      transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'credit' ? '+' : '-'}
                      {formatCurrency(Math.abs(transaction.amount))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Quick Action Button Component
function QuickActionButton({ icon, label }) {
  return (
    <button className="group relative flex flex-col items-center justify-center p-8 bg-white rounded-2xl shadow-sm 
      hover:shadow-xl transition-all duration-500 ease-out transform hover:-translate-y-1 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-transparent 
        opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {/* Decorative circle */}
      <div className="absolute -right-8 -top-8 w-24 h-24 bg-primary-50 rounded-full transform scale-0 
        group-hover:scale-100 transition-transform duration-500 ease-out"></div>
      
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center">
        <div className="w-14 h-14 rounded-xl bg-gray-50 flex items-center justify-center mb-3
          transform group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
          <span className="text-2xl transform group-hover:scale-110 transition-transform duration-300">{icon}</span>
        </div>
        <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600 
          transition-colors duration-300">{label}</span>
      </div>
      
      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full 
        group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
    </button>
  );
} 